// src/components/ExecutiveReport.jsx
import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ============================================================
// CONFIGURACIÓN DE REPORTES DISPONIBLES (BILINGÜE)
// ============================================================
const REPORTES_DISPONIBLES = [
  { id: 'pyg', es: 'Estado de Resultados (PyG)', en: 'Profit & Loss Statement' },
  { id: 'auditoria', es: 'Auditoría Forense', en: 'Forensic Audit' },
  { id: 'kpi', es: 'Tablero de Indicadores KPI', en: 'KPI Dashboard' },
  { id: 'proyecciones', es: 'Proyecciones (30/60/90 Días)', en: '30/60/90 Day Projections' },
  { id: 'inventario', es: 'Valoración de Inventario', en: 'Inventory Valuation' },
  { id: 'cuentasPagar', es: 'Cuentas por Pagar', en: 'Accounts Payable' },
  { id: 'ventas', es: 'Reporte de Ventas', en: 'Sales Report' },
  { id: 'gastos', es: 'Auditoría de Gastos', en: 'Expenses Audit' },
  { id: 'flujoCaja', es: 'Estado de Flujo de Caja', en: 'Cash Flow Statement' },
  { id: 'roi', es: 'ROI por Producto', en: 'ROI per Product' },
  { id: 'compras', es: 'Reporte de Compras', en: 'Purchases Report' }
];

// ============================================================
// HELPERS DE CONFIGURACIÓN PDF
// ============================================================
const crearDocBase = () => new jsPDF();

const estiloTablaCorporativa = {
  theme: 'striped',
  styles: { fontSize: 10, cellPadding: 5 },
  headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
  columnStyles: {
    0: { cellWidth: 90 },
    1: { halign: 'right', cellWidth: 55 },
    2: { halign: 'right', cellWidth: 45 }
  }
};

// ============================================================
// FUNCIÓN PARA AGRUPAR VENTAS POR CANAL
// ============================================================
const agruparVentasPorCanal = (movimientos) => {
  const ventas = movimientos?.filter(m => 
    m.tipo === 'ingreso' && 
    m.tipoOperacion !== 'devolucion_venta' &&
    m.tipoOperacion === 'venta'
  ) || [];

  const porCanal = {};
  ventas.forEach(v => {
    const canal = v.canalVenta || 'Otros';
    if (!porCanal[canal]) {
      porCanal[canal] = { ventas: 0, utilidad: 0, margen: 0 };
    }
    porCanal[canal].ventas += (v.valor || 0);
    // Si tiene utilidad auditada, usarla; si no, estimar
    const utilidad = v.utilidadNetaAuditada || (v.valor * 0.3);
    porCanal[canal].utilidad += utilidad;
  });

  // Calcular margen por canal
  Object.keys(porCanal).forEach(canal => {
    const data = porCanal[canal];
    data.margen = data.ventas > 0 ? ((data.utilidad / data.ventas) * 100) : 0;
  });

  return porCanal;
};

// ============================================================
// FUNCIÓN PRINCIPAL PyG (CONSOLIDADA) - MODIFICADA CON CANALES
// ============================================================
const generarPyG = (doc, data, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, movimientos, usuarioActual, procesosProduccion, ventasPorCanal } = data;
  const { inicio, fin } = getFechasPeriodo();

  const inicioNormalizado = new Date(inicio);
  inicioNormalizado.setHours(0, 0, 0, 0);
  const finNormalizado = new Date(fin);
  finNormalizado.setHours(23, 59, 59, 999);
  const diasPeriodo = Math.max(1, Math.ceil((finNormalizado - inicioNormalizado) / (1000 * 60 * 60 * 24)));

  const convertirFecha = (fecha) => {
    if (!fecha) return null;
    try {
      if (fecha.toDate) return fecha.toDate();
      if (fecha._seconds !== undefined) return new Date(fecha._seconds * 1000);
      if (fecha.seconds !== undefined) return new Date(fecha.seconds * 1000);
      const d = new Date(fecha);
      return isNaN(d.getTime()) ? null : d;
    } catch { return null; }
  };

  const movimientosFiltrados = movimientos?.filter(m => {
    const fecha = convertirFecha(m.fecha || m.actualizado || m.createdAt || m.updatedAt || m.timestamp || m.date);
    return fecha && fecha >= inicioNormalizado && fecha <= finNormalizado;
  }) || [];

  const ingresos = movimientosFiltrados.filter(m => m.tipo === 'ingreso' && m.tipoOperacion !== 'devolucion_venta');
  const devoluciones = movimientosFiltrados.filter(m => m.tipoOperacion === 'devolucion_venta');
  const totalDevVentas = devoluciones.reduce((s, d) => s + (d.valor || 0), 0);
  const ventasBrutas = ingresos.reduce((s, m) => s + (m.valor || 0), 0);
  const ventasNetas = ventasBrutas - totalDevVentas;

  let costoMateriales = 0;
  let costoManoObra = 0;
  let costosFijosAbsorbidos = 0;

  const procesosFiltrados = procesosProduccion?.filter(p => {
    const fecha = convertirFecha(p.fecha || p.actualizado || p.createdAt || p.updatedAt || p.timestamp || p.date || p.fechaInicio);
    return fecha && fecha >= inicioNormalizado && fecha <= finNormalizado;
  }) || [];

  if (procesosFiltrados.length > 0) {
    procesosFiltrados.forEach((proceso) => {
      const valorTotal = proceso.valor || 0;
      let laborCost = parseFloat(proceso.costoManoObra) || 0;
      if (laborCost === 0 && proceso.laborUnitario && proceso.volumenProcesado) {
        laborCost = parseFloat(proceso.laborUnitario) * parseFloat(proceso.volumenProcesado);
      }
      if (laborCost === 0 && proceso.realCostPerHour) {
        laborCost = parseFloat(proceso.realCostPerHour);
      }
      let fixedCost = parseFloat(proceso.costoFijoAbsorbido) || 0;
      if (fixedCost === 0 && proceso.gastosFijosMensuales) {
        fixedCost = (parseFloat(proceso.gastosFijosMensuales) / 30) * diasPeriodo;
      }
      let materiales = valorTotal - laborCost - fixedCost;
      if (materiales < 0) {
        const disponible = valorTotal - laborCost;
        if (disponible > 0) {
          fixedCost = disponible;
          materiales = 0;
        } else {
          laborCost = valorTotal;
          fixedCost = 0;
          materiales = 0;
        }
      }
      costoMateriales += materiales;
      costoManoObra += laborCost;
      costosFijosAbsorbidos += fixedCost;
    });
  } else {
    const egresosProduccion = movimientosFiltrados.filter(m => m.tipo === 'egreso' && m.categoria === 'PRODUCCION');
    if (egresosProduccion.length > 0) {
      const cmvTotal = egresosProduccion.reduce((s, m) => s + (m.valor || 0), 0);
      costoMateriales = cmvTotal * 0.60;
      costoManoObra = cmvTotal * 0.25;
      costosFijosAbsorbidos = cmvTotal * 0.15;
    } else {
      const cmvEstimado = ventasNetas * 0.40;
      costoMateriales = cmvEstimado * 0.60;
      costoManoObra = cmvEstimado * 0.25;
      costosFijosAbsorbidos = cmvEstimado * 0.15;
    }
  }

  const gastosOperativos = movimientosFiltrados.filter(m =>
    m.tipo === 'egreso' &&
    m.categoria !== 'PRODUCCION' &&
    m.categoria !== 'INVENTARIO' &&
    m.categoria !== 'COMPRA' &&
    m.tipoOperacion !== 'compra' &&
    m.tipoOperacion !== 'devolucion_compra'
  );
  const totalGastosOperativos = gastosOperativos.reduce((s, g) => s + (g.valor || 0), 0);

  const costoVentas = costoMateriales + costoManoObra + costosFijosAbsorbidos;
  const utilidadBruta = ventasNetas - costoVentas;
  const margenBruto = ventasNetas > 0 ? ((utilidadBruta / ventasNetas) * 100) : 0;
  const utilidadOperativa = utilidadBruta - totalGastosOperativos;
  const margenOperativo = ventasNetas > 0 ? ((utilidadOperativa / ventasNetas) * 100) : 0;
  const ebitda = utilidadOperativa + (totalGastosOperativos * 0.05);
  const margenEBITDA = ventasNetas > 0 ? ((ebitda / ventasNetas) * 100) : 0;
  const utilidadNeta = utilidadOperativa;
  const margenNeto = ventasNetas > 0 ? ((utilidadNeta / ventasNetas) * 100) : 0;
  const puntoEquilibrio = (margenBruto > 0 && totalGastosOperativos > 0) ? totalGastosOperativos / (margenBruto / 100) : 0;
  const capitalInyectado = usuarioActual?.aportesPersonales || 0;
  const roi = capitalInyectado > 0 ? ((utilidadNeta / capitalInyectado) * 100) : null;
  const requiereCapitalInicial = capitalInyectado <= 0 && utilidadNeta !== 0;

  // ============================================================
  // CÁLCULO DE VENTAS POR CANAL
  // ============================================================
  const ventasPorCanalData = ventasPorCanal || agruparVentasPorCanal(movimientos);
  const canales = Object.keys(ventasPorCanalData);
  
  // Encontrar el mejor y peor canal
  let mejorCanal = '';
  let peorCanal = '';
  let mejorMargen = -Infinity;
  let peorMargen = Infinity;
  
  canales.forEach(canal => {
    const data = ventasPorCanalData[canal];
    if (data.margen > mejorMargen) {
      mejorMargen = data.margen;
      mejorCanal = canal;
    }
    if (data.margen < peorMargen && data.ventas > 0) {
      peorMargen = data.margen;
      peorCanal = canal;
    }
  });

  // ============================================================
  // OBJETO dataFinanciera PARA REPORTES SATÉLITE
  // ============================================================
  const dataFinanciera = {
    formatFecha,
    formatMoney,
    getFechasPeriodo,
    ventasNetas,
    costoVentas,
    utilidadBruta,
    gastosOperativos: totalGastosOperativos,
    utilidadNeta,
    utilidadOperativa,
    margenBrutoPct: margenBruto,
    margenNetoPct: margenNeto,
    totalDevVentas,
    puntoEquilibrio,
    roi,
    requiereCapitalInicial,
    ingresos,
    inventario: data.inventario || [],
    tendenciaCrecimiento: 1.08,
    movimientos: data.movimientos || [],
    usuarioActual: data.usuarioActual || {},
    ventasPorCanal: ventasPorCanalData,
    mejorCanal,
    peorCanal,
    mejorMargen,
    peorMargen
  };

  // GENERAR PDF PyG
  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'PROFIT & LOSS STATEMENT' : 'ESTADO DE RESULTADOS (PyG)', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  const head = [[isEn ? 'Concept' : 'Concepto', isEn ? 'Value' : 'Valor', isEn ? 'Margin' : 'Margen']];
  const body = [
    [isEn ? 'TOTAL SALES' : 'VENTAS TOTALES', formatMoney(ventasNetas), ''],
    [isEn ? 'COST OF SALES (CMV)' : 'COSTO DE VENTAS (CMV)', formatMoney(costoVentas), ''],
    [isEn ? '  Material Costs' : '  Costo de Materiales', formatMoney(costoMateriales), ''],
    [isEn ? '  Labor Costs (MOD)' : '  Mano de Obra (MOD)', formatMoney(costoManoObra), ''],
    [isEn ? '  Absorbed Fixed Expenses' : '  Gastos Fijos Absorbidos', formatMoney(costosFijosAbsorbidos), ''],
    [isEn ? 'GROSS PROFIT' : 'UTILIDAD BRUTA', formatMoney(utilidadBruta), margenBruto.toFixed(1) + '%'],
    [isEn ? 'Operating Expenses' : 'GASTOS OPERATIVOS', formatMoney(totalGastosOperativos), ''],
    [isEn ? 'OPERATING INCOME' : 'UTILIDAD OPERATIVA', formatMoney(utilidadOperativa), margenOperativo.toFixed(1) + '%'],
    [isEn ? 'EBITDA' : 'EBITDA', formatMoney(ebitda), margenEBITDA.toFixed(1) + '%'],
    [isEn ? 'NET INCOME' : 'UTILIDAD NETA', formatMoney(utilidadNeta), margenNeto.toFixed(1) + '%'],
    [isEn ? 'BREAK-EVEN POINT' : 'PUNTO DE EQUILIBRIO', puntoEquilibrio > 0 ? formatMoney(puntoEquilibrio) : '$0', ''],
    [isEn ? 'ROI' : 'ROI', roi !== null ? roi.toFixed(1) + '%' : 'N/A', '']
  ];

  doc.autoTable({ startY: 65, head, body, ...estiloTablaCorporativa });

  // ============================================================
  // SECCIÓN DE VENTAS POR CANAL EN PDF
  // ============================================================
  let yPos = doc.lastAutoTable.finalY + 15;
  
  if (canales.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(isEn ? 'SALES BY CHANNEL' : 'VENTAS POR CANAL', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    canales.forEach(canal => {
      const data = ventasPorCanalData[canal];
      const ventas = data.ventas || 0;
      const porcentaje = ventasNetas > 0 ? ((ventas / ventasNetas) * 100) : 0;
      const margen = data.margen || 0;
      
      const emoji = margen >= 50 ? '✅' : margen >= 30 ? '⚠️' : '❌';
      const color = margen >= 50 ? 'Green' : margen >= 30 ? 'DarkGoldenrod' : 'Red';
      
      doc.setTextColor(0, 0, 0);
      doc.text(`${emoji} ${canal}:`, 25, yPos);
      doc.setTextColor(50, 50, 50);
      doc.text(`${formatMoney(ventas)} (${porcentaje.toFixed(1)}%) - Margen:`, 25 + doc.getTextWidth(`${emoji} ${canal}: `) + 5, yPos);
      
      doc.setTextColor(color === 'Green' ? 0 : color === 'Red' ? 200 : 150, 
                        color === 'Green' ? 150 : color === 'Red' ? 50 : 100, 
                        color === 'Green' ? 50 : color === 'Red' ? 50 : 0);
      doc.text(`${margen.toFixed(1)}%`, 25 + doc.getTextWidth(`${emoji} ${canal}: ${formatMoney(ventas)} (${porcentaje.toFixed(1)}%) - Margen: `) + 5, yPos);
      
      yPos += 7;
      doc.setTextColor(0, 0, 0);
    });
    
    // Recomendación
    if (mejorCanal && mejorCanal !== peorCanal) {
      yPos += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 150, 50);
      doc.text(isEn ? `✅ Best Channel: ${mejorCanal} (${mejorMargen.toFixed(1)}% margin)` : `✅ Mejor Canal: ${mejorCanal} (${mejorMargen.toFixed(1)}% margen)`, 25, yPos);
      yPos += 6;
      
      if (peorCanal && peorMargen < 30) {
        doc.setTextColor(200, 100, 0);
        doc.text(isEn ? `⚠️ Channel with low margin: ${peorCanal} (${peorMargen.toFixed(1)}%) - Review pricing or commissions` : `⚠️ Canal con bajo margen: ${peorCanal} (${peorMargen.toFixed(1)}%) - Revisar precios o comisiones`, 25, yPos);
      }
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    }
  }

  if (requiereCapitalInicial) {
    const yNota = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(9);
    doc.setTextColor(220, 120, 0);
    doc.setFont('helvetica', 'italic');
    let msg = isEn
      ? 'Requires entering Initial Capital in balance settings to project real ROI.'
      : 'Requiere ingresar el Capital Inicial en la configuracion del balance para proyectar el ROI real.';
    msg = msg.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    doc.text(msg, 20, yNota);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }

  return dataFinanciera;
};

// ============================================================
// REPORTE: AUDITORÍA FORENSE (MODIFICADO CON CANALES)
// ============================================================
const generarAuditoria = (doc, dataFinanciera, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, ventasNetas, gastosOperativos, utilidadBruta, utilidadOperativa, margenBrutoPct, margenNetoPct, totalDevVentas, ventasPorCanal, mejorCanal, peorCanal, mejorMargen, peorMargen } = dataFinanciera;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'FORENSIC AUDIT' : 'AUDITORIA FORENSE', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  const gastosPorcentaje = ventasNetas > 0 ? (gastosOperativos / ventasNetas) * 100 : 0;
  const margenBrutoNum = parseFloat(margenBrutoPct) || 0;
  const margenNetoNum = parseFloat(margenNetoPct) || 0;

  const anomalias = [];

  if (margenBrutoNum < 30) {
    anomalias.push({
      concepto: isEn ? 'Gross Margin' : 'Margen Bruto',
      estado: isEn
        ? `Current Gross Margin ${margenBrutoNum.toFixed(1)}% - Below 30% threshold - Low pricing power`
        : `Margen Bruto actual de ${margenBrutoNum.toFixed(1)}% - Por debajo del umbral del 30% - Bajo poder de fijacion de precios`,
      nivel: 'ALTO'
    });
  } else {
    anomalias.push({
      concepto: isEn ? 'Gross Margin' : 'Margen Bruto',
      estado: isEn
        ? `Current Gross Margin ${margenBrutoNum.toFixed(1)}% - Above 30% threshold - Healthy pricing power`
        : `Margen Bruto actual de ${margenBrutoNum.toFixed(1)}% - Por encima del umbral del 30% - Buen poder de fijacion de precios`,
      nivel: 'BAJO'
    });
  }

  if (margenNetoNum < 0) {
    anomalias.push({
      concepto: isEn ? 'Net Margin' : 'Margen Neto',
      estado: isEn
        ? `Current Net Margin ${margenNetoNum.toFixed(1)}% - Negative profitability - Critical risk`
        : `Margen Neto actual de ${margenNetoNum.toFixed(1)}% - Rentabilidad negativa - Riesgo critico`,
      nivel: 'CRITICO'
    });
  } else if (margenNetoNum < 10) {
    anomalias.push({
      concepto: isEn ? 'Net Margin' : 'Margen Neto',
      estado: isEn
        ? `Current Net Margin ${margenNetoNum.toFixed(1)}% - Below 10% threshold - Low profitability risk`
        : `Margen Neto actual de ${margenNetoNum.toFixed(1)}% - Por debajo del umbral del 10% - Riesgo de baja rentabilidad`,
      nivel: 'ALTO'
    });
  } else {
    anomalias.push({
      concepto: isEn ? 'Net Margin' : 'Margen Neto',
      estado: isEn
        ? `Current Net Margin ${margenNetoNum.toFixed(1)}% - Above 10% threshold - Healthy profitability`
        : `Margen Neto actual de ${margenNetoNum.toFixed(1)}% - Por encima del umbral del 10% - Rentabilidad saludable`,
      nivel: 'BAJO'
    });
  }

  if (totalDevVentas > 0) {
    anomalias.push({
      concepto: isEn ? 'Sales Returns' : 'Devoluciones en Venta',
      estado: isEn ? formatMoney(totalDevVentas) + ' - Review quality' : formatMoney(totalDevVentas) + ' - Revisar calidad',
      nivel: 'MEDIO'
    });
  }

  if (gastosPorcentaje === 0) {
    anomalias.push({
      concepto: isEn ? 'Operating Expenses' : 'Gastos Operativos',
      estado: isEn ? '0% of sales - Optimal operational structure' : '0% de las ventas - Estructura operativa optima',
      nivel: 'BAJO'
    });
  } else if (gastosPorcentaje <= 30) {
    anomalias.push({
      concepto: isEn ? 'Operating Expenses' : 'Gastos Operativos',
      estado: isEn
        ? `${gastosPorcentaje.toFixed(1)}% of sales - Efficient operational structure`
        : `${gastosPorcentaje.toFixed(1)}% de las ventas - Estructura operativa eficiente`,
      nivel: 'BAJO'
    });
  } else if (gastosPorcentaje <= 40) {
    anomalias.push({
      concepto: isEn ? 'Operating Expenses' : 'Gastos Operativos',
      estado: isEn
        ? `${gastosPorcentaje.toFixed(1)}% of sales - Moderate operational structure - Review costs`
        : `${gastosPorcentaje.toFixed(1)}% de las ventas - Estructura operativa moderada - Revisar costos`,
      nivel: 'MEDIO'
    });
  } else {
    anomalias.push({
      concepto: isEn ? 'Operating Expenses' : 'Gastos Operativos',
      estado: isEn
        ? `${gastosPorcentaje.toFixed(1)}% of sales - Exceeds 40% threshold - Immediate cost review required`
        : `${gastosPorcentaje.toFixed(1)}% de las ventas - Supera el umbral del 40% - Revision de costos inmediata requerida`,
      nivel: 'ALTO'
    });
  }

  if (utilidadBruta > 0 && utilidadOperativa < 0) {
    anomalias.push({
      concepto: isEn ? 'Operating Result' : 'Resultado Operativo',
      estado: isEn ? 'Gross profit positive but operating loss - Fixed costs too high' : 'Utilidad bruta positiva pero perdida operativa - Costos fijos demasiado altos',
      nivel: 'CRITICO'
    });
  }

  // ============================================================
  // ANÁLISIS DE CANALES
  // ============================================================
  if (ventasPorCanal && Object.keys(ventasPorCanal).length > 0) {
    const canales = Object.keys(ventasPorCanal);
    
    // Canal con mayor margen
    let mejorCanalNombre = '';
    let mejorCanalMargen = -Infinity;
    let peorCanalNombre = '';
    let peorCanalMargen = Infinity;
    
    canales.forEach(canal => {
      const data = ventasPorCanal[canal];
      if (data.margen > mejorCanalMargen && data.ventas > 0) {
        mejorCanalMargen = data.margen;
        mejorCanalNombre = canal;
      }
      if (data.margen < peorCanalMargen && data.ventas > 0) {
        peorCanalMargen = data.margen;
        peorCanalNombre = canal;
      }
    });
    
    if (mejorCanalNombre) {
      anomalias.push({
        concepto: isEn ? 'Best Channel' : 'Mejor Canal',
        estado: isEn
          ? `${mejorCanalNombre} with ${mejorCanalMargen.toFixed(1)}% margin - ${mejorCanalMargen >= 50 ? 'Excellent' : 'Good'}`
          : `${mejorCanalNombre} con ${mejorCanalMargen.toFixed(1)}% margen - ${mejorCanalMargen >= 50 ? 'Excelente' : 'Bueno'}`,
        nivel: mejorCanalMargen >= 50 ? 'BAJO' : 'MEDIO'
      });
    }
    
    if (peorCanalNombre && peorCanalMargen < 30) {
      anomalias.push({
        concepto: isEn ? 'Channel with low margin' : 'Canal con bajo margen',
        estado: isEn
          ? `${peorCanalNombre} with ${peorCanalMargen.toFixed(1)}% margin - Review pricing or commissions`
          : `${peorCanalNombre} con ${peorCanalMargen.toFixed(1)}% margen - Revisar precios o comisiones`,
        nivel: 'ALTO'
      });
    }
  }

  const head = [[isEn ? 'Risk Concept' : 'Concepto de Riesgo', isEn ? 'Status' : 'Estado', isEn ? 'Level' : 'Nivel']];
  const body = anomalias.map(a => [a.concepto, a.estado, a.nivel]);

  doc.autoTable({ startY: 65, head, body, ...estiloTablaCorporativa });
};

// ============================================================
// REPORTE: KPI DASHBOARD - MODIFICADO CON CANALES
// ============================================================
const generarKPI = (doc, dataFinanciera, isEn) => {
  const { 
    formatFecha, 
    formatMoney, 
    getFechasPeriodo, 
    ventasNetas, 
    gastosOperativos, 
    ingresos, 
    inventario, 
    utilidadNeta, 
    roi, 
    puntoEquilibrio, 
    requiereCapitalInicial,
    costoVentas,
    costosFijosAbsorbidos,
    ventasPorCanal,
    mejorCanal,
    mejorMargen,
    peorCanal,
    peorMargen
  } = dataFinanciera;
  
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'KPI DASHBOARD' : 'TABLERO DE INDICADORES KPI', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  const transaccionesCount = ingresos?.length > 0 ? ingresos.length : 1;
  const ticketPromedio = ventasNetas / transaccionesCount;
  
  let rotacionAbsorcion = 0;
  if (gastosOperativos > 0) {
    rotacionAbsorcion = ventasNetas / gastosOperativos;
  } else if (costoVentas > 0 && inventario?.length > 0) {
    const valorInventario = inventario.reduce((sum, item) => sum + ((item.costoUnitario || 0) * (item.cantidad || 0)), 0);
    if (valorInventario > 0) {
      rotacionAbsorcion = costoVentas / valorInventario;
    } else {
      const totalUnidades = inventario.reduce((sum, item) => sum + (item.cantidad || 0), 0);
      if (totalUnidades > 0) {
        rotacionAbsorcion = ventasNetas / totalUnidades;
      }
    }
  }
  const rotacionAbsorcionFormateada = rotacionAbsorcion > 0 ? rotacionAbsorcion.toFixed(2) + 'x' : '0x';

  const margenNetoReal = ventasNetas > 0 ? ((utilidadNeta / ventasNetas) * 100) : 0;

  let puntoEquilibrioCalculado = 0;
  const margenBrutoPct = dataFinanciera.margenBrutoPct || 0;
  const costosFijos = costosFijosAbsorbidos || gastosOperativos || 0;
  
  if (margenBrutoPct > 0 && costosFijos > 0) {
    puntoEquilibrioCalculado = costosFijos / (margenBrutoPct / 100);
  } else if (margenNetoReal > 0 && costosFijos > 0) {
    puntoEquilibrioCalculado = costosFijos / (margenNetoReal / 100);
  }
  const puntoEquilibrioFinal = puntoEquilibrioCalculado > 0 ? puntoEquilibrioCalculado : (puntoEquilibrio || 0);

  const head = [[isEn ? 'Indicator' : 'Indicador', isEn ? 'Value' : 'Valor']];
  const body = [
    [isEn ? 'Average Ticket' : 'Ticket Promedio', formatMoney(ticketPromedio)],
    [isEn ? 'Net Margin' : 'Margen Neto', margenNetoReal.toFixed(1) + '%'],
    [isEn ? 'Absorption Rate' : 'Tasa de Absorcion', rotacionAbsorcionFormateada],
    [isEn ? 'Products in Inventory' : 'Productos en Inventario', String(inventario?.length || 0)],
    [isEn ? 'Sales Transactions' : 'Transacciones de Venta', String(ingresos?.length || 0)],
    [isEn ? 'ROI' : 'ROI', roi !== null ? (typeof roi === 'string' ? roi : roi.toFixed(1) + '%') : 'N/A'],
    [isEn ? 'Break-even Point' : 'Punto de Equilibrio', puntoEquilibrioFinal > 0 ? formatMoney(puntoEquilibrioFinal) : '$0']
  ];

  // Añadir información de canales si existe
  if (ventasPorCanal && Object.keys(ventasPorCanal).length > 0) {
    body.push([isEn ? 'Best Channel' : 'Mejor Canal', mejorCanal ? `${mejorCanal} (${mejorMargen.toFixed(1)}%)` : 'N/A']);
    if (peorCanal && peorMargen < 30) {
      body.push([isEn ? 'Channel to Review' : 'Canal a Revisar', `${peorCanal} (${peorMargen.toFixed(1)}%)`]);
    }
  }

  doc.autoTable({ startY: 65, head, body, ...estiloTablaCorporativa });

  // Nota explicativa si la tasa de absorción es 0
  if (rotacionAbsorcion === 0 && gastosOperativos === 0) {
    const yNota = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'italic');
    const msg = isEn 
      ? '* Absorption rate calculated from CMV/Inventory ratio as no operating expenses were recorded.'
      : '* Tasa de absorción calculada desde la relación CMV/Inventario ya que no se registraron gastos operativos.';
    doc.text(msg, 20, yNota);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }

  if (requiereCapitalInicial) {
    const yNota = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(9);
    doc.setTextColor(220, 120, 0);
    doc.setFont('helvetica', 'italic');
    const msg = isEn ? 'Requires entering Initial Capital in balance settings to project real ROI.' : 'Requiere ingresar el Capital Inicial en la configuracion del balance para proyectar el ROI real.';
    doc.text(msg, 20, yNota);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }

  let y = doc.lastAutoTable.finalY + 15;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(isEn ? 'SALES vs EXPENSES' : 'VENTAS vs GASTOS', 20, y);
  y += 10;
  doc.setFont('helvetica', 'normal');

  const maxValor = Math.max(ventasNetas, gastosOperativos || costoVentas || 1);
  const anchoMaximo = 140;
  if (maxValor > 0) {
    doc.setFillColor(34, 197, 94);
    doc.rect(20, y, (ventasNetas / maxValor) * anchoMaximo, 8, 'F');
    doc.text(isEn ? 'Sales' : 'Ventas', 20 + (ventasNetas / maxValor) * anchoMaximo + 5, y + 5);
    y += 12;
    doc.setFillColor(239, 68, 68);
    const gastosValor = gastosOperativos > 0 ? gastosOperativos : costoVentas;
    doc.rect(20, y, (gastosValor / maxValor) * anchoMaximo, 8, 'F');
    doc.text(isEn ? 'Expenses' : 'Gastos', 20 + (gastosValor / maxValor) * anchoMaximo + 5, y + 5);
  }
};

// ============================================================
// REPORTE: PROYECCIONES
// ============================================================
const generarProyecciones = (doc, dataFinanciera, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, ventasNetas, utilidadNeta, tendenciaCrecimiento } = dataFinanciera;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? '30/60/90 DAY PROJECTIONS' : 'PROYECCIONES (30/60/90 DIAS)', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  const margenNetoReal = ventasNetas > 0 ? ((utilidadNeta / ventasNetas) * 100) : 0;
  const factor = tendenciaCrecimiento || 1.08;
  const proyeccion30 = ventasNetas * factor;
  const proyeccion60 = proyeccion30 * factor;
  const proyeccion90 = proyeccion60 * factor;

  const head = [[isEn ? 'Period' : 'Periodo', isEn ? 'Projected Sales' : 'Ventas Proyectadas', isEn ? 'Expected Margin' : 'Margen Esperado']];
  const body = [
    [isEn ? 'Current' : 'Actual', formatMoney(ventasNetas), margenNetoReal.toFixed(1) + '%'],
    [isEn ? '30 days' : '30 dias', formatMoney(proyeccion30), margenNetoReal.toFixed(1) + '%'],
    [isEn ? '60 days' : '60 dias', formatMoney(proyeccion60), margenNetoReal.toFixed(1) + '%'],
    [isEn ? '90 days' : '90 dias', formatMoney(proyeccion90), margenNetoReal.toFixed(1) + '%']
  ];

  doc.autoTable({ startY: 65, head, body, ...estiloTablaCorporativa });

  const y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  const msg = isEn ? '* Projections based on current growth trend (' + ((factor - 1) * 100).toFixed(0) + '%)' : '* Proyecciones basadas en la tendencia de crecimiento actual (' + ((factor - 1) * 100).toFixed(0) + '%)';
  doc.text(msg, 20, y);
};

// ============================================================
// REPORTE: FLUJO DE CAJA
// ============================================================
const generarFlujoCaja = (doc, dataFinanciera, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, ventasNetas, costoVentas, gastosOperativos, utilidadNeta } = dataFinanciera;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'CASH FLOW STATEMENT' : 'ESTADO DE FLUJO DE CAJA', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  const head = [[isEn ? 'Concept' : 'Concepto', isEn ? 'Inflow' : 'Entrada', isEn ? 'Outflow' : 'Salida']];
  const body = [
    [isEn ? 'Sales' : 'Ventas', formatMoney(ventasNetas), ''],
    [isEn ? 'Purchases / Production' : 'Compras / Produccion', '', formatMoney(costoVentas)],
    [isEn ? 'Operating Expenses' : 'Gastos Operativos', '', formatMoney(gastosOperativos)],
    [isEn ? 'NET CASH FLOW' : 'FLUJO NETO', '', formatMoney(utilidadNeta)]
  ];

  doc.autoTable({ startY: 65, head, body, ...estiloTablaCorporativa });
};

// ============================================================
// REPORTE: INVENTARIO
// ============================================================
const generarInventario = (doc, data, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, inventario } = data;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'INVENTORY VALUATION' : 'VALORACION DE INVENTARIO', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  if (!inventario || inventario.length === 0) {
    doc.setFontSize(10);
    doc.text(isEn ? 'No inventory data available.' : 'No hay datos de inventario disponibles.', 20, 65);
    return;
  }

  const head = [[isEn ? 'Product' : 'Producto', isEn ? 'Quantity' : 'Cantidad', isEn ? 'Unit Cost' : 'Costo Unitario', isEn ? 'Total Value' : 'Valor Total']];
  const body = inventario.map(item => [item.producto || '-', String(item.cantidad || 0), formatMoney(item.costoUnitario || 0), formatMoney((item.costoUnitario || 0) * (item.cantidad || 0))]);

  doc.autoTable({
    startY: 65, head, body,
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
    columnStyles: { 0: { cellWidth: 70 }, 1: { halign: 'center', cellWidth: 30 }, 2: { halign: 'right', cellWidth: 45 }, 3: { halign: 'right', cellWidth: 45 } }
  });

  const y = doc.lastAutoTable.finalY + 10;
  const valorTotal = inventario.reduce((sum, item) => sum + ((item.costoUnitario || 0) * (item.cantidad || 0)), 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text((isEn ? 'Total Inventory Value: ' : 'Valor Total del Inventario: ') + formatMoney(valorTotal), 20, y);
};

// ============================================================
// REPORTE: CUENTAS POR PAGAR
// ============================================================
const generarCuentasPagar = (doc, data, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, cuentasPorPagar } = data;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'ACCOUNTS PAYABLE' : 'CUENTAS POR PAGAR', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  if (!cuentasPorPagar || cuentasPorPagar.length === 0) {
    doc.setFontSize(10);
    doc.text(isEn ? 'No accounts payable data available.' : 'No hay datos de cuentas por pagar disponibles.', 20, 65);
    return;
  }

  const head = [[isEn ? 'Supplier' : 'Proveedor', isEn ? 'Concept' : 'Concepto', isEn ? 'Amount' : 'Monto', isEn ? 'Due Date' : 'Vencimiento', isEn ? 'Status' : 'Estado']];
  const body = cuentasPorPagar.map(cuenta => [cuenta.proveedor || '-', cuenta.concepto || '-', formatMoney(cuenta.valor || 0), formatFecha(cuenta.fechaVencimiento), cuenta.estado || (isEn ? 'PENDING' : 'PENDIENTE')]);

  doc.autoTable({ startY: 65, head, body, theme: 'striped', styles: { fontSize: 9, cellPadding: 4 }, headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] } });

  const y = doc.lastAutoTable.finalY + 10;
  const totalPagar = cuentasPorPagar.reduce((sum, c) => sum + (c.valor || 0), 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text((isEn ? 'Total Payable: ' : 'Total por Pagar: ') + formatMoney(totalPagar), 20, y);
};

// ============================================================
// REPORTE: VENTAS
// ============================================================
const generarVentas = (doc, data, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, ingresos, ventasNetas } = data;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'SALES REPORT' : 'REPORTE DE VENTAS', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  if (ingresos.length === 0) {
    doc.setFontSize(10);
    doc.text(isEn ? 'No sales data available.' : 'No hay datos de ventas disponibles.', 20, 65);
    return;
  }

  const head = [[isEn ? 'Date' : 'Fecha', isEn ? 'Concept' : 'Concepto', isEn ? 'Category' : 'Categoria', isEn ? 'Channel' : 'Canal', isEn ? 'Amount' : 'Monto']];
  const body = ingresos.slice(0, 50).map(m => [
    formatFecha(m.fecha), 
    m.concepto || '-', 
    m.categoria || '-', 
    m.canalVenta || (m.plataforma || '-'),
    formatMoney(m.valor || 0)
  ]);

  doc.autoTable({ startY: 65, head, body, theme: 'striped', styles: { fontSize: 9, cellPadding: 4 }, headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] } });

  const y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text((isEn ? 'Total Sales: ' : 'Total Ventas: ') + formatMoney(ventasNetas), 20, y);
};

// ============================================================
// REPORTE: GASTOS
// ============================================================
const generarGastos = (doc, data, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, egresos, totalGastosOperativos } = data;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'EXPENSES AUDIT' : 'AUDITORIA DE GASTOS', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  if (egresos.length === 0) {
    doc.setFontSize(10);
    doc.text(isEn ? 'No expenses data available.' : 'No hay datos de gastos disponibles.', 20, 65);
    return;
  }

  const head = [[isEn ? 'Date' : 'Fecha', isEn ? 'Concept' : 'Concepto', isEn ? 'Category' : 'Categoria', isEn ? 'Amount' : 'Monto']];
  const body = egresos.slice(0, 50).map(m => [formatFecha(m.fecha), m.concepto || '-', m.categoria || '-', formatMoney(m.valor || 0)]);

  doc.autoTable({ startY: 65, head, body, theme: 'striped', styles: { fontSize: 9, cellPadding: 4 }, headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] } });

  const y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text((isEn ? 'Total Expenses: ' : 'Total Gastos: ') + formatMoney(totalGastosOperativos), 20, y);
};

// ============================================================
// REPORTE: COMPRAS
// ============================================================
const generarCompras = (doc, data, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, movimientos, compras } = data;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'PRODUCTION / SUPPLY REPORT' : 'REPORTE DE PRODUCCION / ABASTECIMIENTO', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  const convertirFecha = (fecha) => {
    if (!fecha) return null;
    try {
      if (fecha.toDate) return fecha.toDate();
      if (fecha._seconds !== undefined) return new Date(fecha._seconds * 1000);
      if (fecha.seconds !== undefined) return new Date(fecha.seconds * 1000);
      const d = new Date(fecha);
      return isNaN(d.getTime()) ? null : d;
    } catch { return null; }
  };

  let registrosCompras = [];

  if (compras && compras.length > 0) {
    registrosCompras = compras;
  } 
  else if (movimientos && movimientos.length > 0) {
    const terminosAbastecimiento = [
      'compra', 'purchase', 'buy', 'adquisicion', 'supplier', 'proveedor',
      'produccion', 'production', 'fabricacion', 'manufactura', 'manufacturing',
      'inventario', 'inventory', 'stock', 'almacen', 'warehouse',
      'materia prima', 'raw material', 'insumo', 'input'
    ];
    
    registrosCompras = movimientos.filter(m => {
      const tipo = (m.tipo || '').toLowerCase();
      const categoria = (m.categoria || '').toLowerCase();
      const concepto = (m.concepto || '').toLowerCase();
      const tipoOperacion = (m.tipoOperacion || '').toLowerCase();
      
      const esEgreso = tipo === 'egreso' || tipo === 'salida' || tipo === 'out' || tipo === 'gasto';
      
      const contieneTermino = terminosAbastecimiento.some(termino => 
        categoria.includes(termino) || 
        concepto.includes(termino) || 
        tipoOperacion.includes(termino)
      );
      
      const esAbastecimientoExplicito = categoria === 'compra' || 
                                        categoria === 'produccion' || 
                                        categoria === 'production' ||
                                        categoria === 'inventario' ||
                                        categoria === 'fabricacion' ||
                                        tipoOperacion === 'compra' ||
                                        tipoOperacion === 'produccion' ||
                                        tipoOperacion === 'purchase';
      
      return esEgreso && (esAbastecimientoExplicito || contieneTermino);
    });
  }

  let registrosFiltrados = registrosCompras;
  if (inicio && fin && registrosCompras.length > 0) {
    registrosFiltrados = registrosCompras.filter(item => {
      const fechaItem = convertirFecha(item.fecha);
      if (!fechaItem) return true;
      return fechaItem >= inicio && fechaItem <= fin;
    });
  }

  if (registrosFiltrados.length === 0) {
    doc.setFontSize(10);
    const msg = isEn 
      ? `No production or supply records found in the selected period.`
      : `No hay registros de produccion o abastecimiento en el periodo seleccionado.`;
    doc.text(msg, 20, 65);
    return;
  }

  const head = [[
    isEn ? 'Product / Concept' : 'Producto / Concepto',
    isEn ? 'Quantity' : 'Cantidad',
    isEn ? 'Unit Cost' : 'Costo Unitario',
    isEn ? 'Total Investment' : 'Total Inversion'
  ]];

  let totalInversion = 0;
  const body = registrosFiltrados.slice(0, 100).map(item => {
    const cantidad = parseFloat(item.cantidad) || parseFloat(item.valor) / parseFloat(item.costoUnitario) || 0;
    const costoUnitario = parseFloat(item.costoUnitario) || 0;
    const total = cantidad * costoUnitario;
    totalInversion += total;

    const nombre = item.producto || item.concepto || item.nombre || 'Produccion';
    const esProduccion = (item.categoria || '').toLowerCase().includes('produccion') || 
                         (item.concepto || '').toLowerCase().includes('produccion');
    
    const nombreFinal = esProduccion ? 
      (isEn ? `Production: ${nombre}` : `Produccion: ${nombre}`) : 
      nombre;

    return [
      nombreFinal,
      String(cantidad > 0 ? cantidad : 1),
      formatMoney(costoUnitario > 0 ? costoUnitario : (total / (cantidad || 1))),
      formatMoney(total > 0 ? total : item.valor || 0)
    ];
  });

  body.push(['', '', '', '']);
  body.push([
    isEn ? 'TOTAL PRODUCTION / SUPPLY' : 'TOTAL PRODUCCION / ABASTECIMIENTO',
    '',
    '',
    formatMoney(totalInversion)
  ]);

  doc.autoTable({
    startY: 65,
    head,
    body,
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] }
  });
};

// ============================================================
// REPORTE: ROI POR PRODUCTO
// ============================================================
const generarROI = (doc, data, isEn) => {
  const { formatFecha, formatMoney, getFechasPeriodo, inventario, ventasNetas } = data;
  const { inicio, fin } = getFechasPeriodo();

  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.text(isEn ? 'ROI PER PRODUCT' : 'ROI POR PRODUCTO', 105, 32, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((isEn ? 'Period: ' : 'Periodo: ') + formatFecha(inicio) + (isEn ? ' to ' : ' hasta ') + formatFecha(fin), 105, 42, { align: 'center' });
  doc.text((isEn ? 'Generated: ' : 'Fecha de generacion: ') + new Date().toLocaleString(), 105, 50, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  const head = [[
    isEn ? 'Product / Concept' : 'Producto / Concepto',
    isEn ? 'Investment' : 'Inversion',
    isEn ? 'ROI' : 'ROI'
  ]];
  
  let body = [];

  const costoTotalProductos = inventario?.reduce((sum, item) => sum + ((item.costoUnitario || 0) * (item.cantidad || 0)), 0) || 0;

  if (inventario && inventario.length > 0) {
    const sorted = [...inventario].sort((a, b) => ((b.costoUnitario || 0) * (b.cantidad || 0)) - ((a.costoUnitario || 0) * (a.cantidad || 0))).slice(0, 10);
    
    sorted.forEach(item => {
      const inversionItem = (item.costoUnitario || 0) * (item.cantidad || 0);
      const roiItem = costoTotalProductos > 0 ? ((inversionItem / costoTotalProductos) * 100) : 0;
      
      body.push([
        item.producto || '-',
        formatMoney(inversionItem),
        roiItem.toFixed(2) + '%'
      ]);
    });
  }

  const inversionTotal = costoTotalProductos; 
  const ingresosReales = ventasNetas || 0; 
  const utilidadNetaCalculada = ingresosReales - inversionTotal;
  const roiGlobal = inversionTotal > 0 ? ((utilidadNetaCalculada / inversionTotal) * 100) : 0;

  body.push(['', '', '']);
  body.push([
    isEn ? 'TOTAL INVESTMENT (CMV)' : 'INVERSION TOTAL (CMV)',
    formatMoney(inversionTotal),
    ''
  ]);
  body.push([
    isEn ? 'NET PROFIT' : 'UTILIDAD NETA',
    formatMoney(utilidadNetaCalculada),
    ''
  ]);
  body.push([
    isEn ? 'GLOBAL ROI' : 'ROI GLOBAL',
    '',
    roiGlobal.toFixed(2) + '%'
  ]);

  doc.autoTable({
    startY: 65,
    head,
    body,
    theme: 'striped',
    styles: { fontSize: 10, cellPadding: 5 },
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] }
  });
};

// ============================================================
// GENERADOR DE REPORTES SELECCIONADOS (COMPILACIÓN DINÁMICA)
// ============================================================
export const generarReportesSeleccionados = (selectedReports, data, isEn) => {
  const doc = crearDocBase();
  const generadores = {
    pyg: generarPyG,
    auditoria: generarAuditoria,
    kpi: generarKPI,
    proyecciones: generarProyecciones,
    inventario: generarInventario,
    cuentasPagar: generarCuentasPagar,
    ventas: generarVentas,
    gastos: generarGastos,
    flujoCaja: generarFlujoCaja,
    roi: generarROI,
    compras: generarCompras
  };

  let dataFinanciera = null;

  selectedReports.forEach((reportId, index) => {
    if (index > 0) doc.addPage();

    if (reportId === 'pyg') {
      dataFinanciera = generadores[reportId](doc, data, isEn);
    } else if (['auditoria', 'kpi', 'proyecciones', 'flujoCaja'].includes(reportId)) {
      if (!dataFinanciera) {
        const tempDoc = crearDocBase();
        dataFinanciera = generarPyG(tempDoc, data, isEn);
      }
      generadores[reportId](doc, dataFinanciera, isEn);
    } else {
      generadores[reportId](doc, data, isEn);
    }
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text((isEn ? 'Page ' : 'Pagina ') + i + (isEn ? ' of ' : ' de ') + totalPages, 105, 290, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  const fecha = new Date();
  const nombreArchivo = `Stratium_Reporte_${fecha.toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
  doc.save(nombreArchivo);
};

// ============================================================
// MOTOR DE REPORTES LEGACY
// ============================================================
export const generarReporte = async (
  tipo, usuarioActual, movimientos, cuentasPorPagar, inventario, idioma,
  capitalInyectado = 0, rotacionInventario = 0, tendenciaCrecimiento = 1.08,
  procesosProduccion = []
) => {
  const doc = crearDocBase();
  const isEn = idioma === 'en';

  const ingresos = movimientos?.filter(m => m.tipo === 'ingreso' && m.tipoOperacion !== 'devolucion_venta') || [];
  const egresos = movimientos?.filter(m => m.tipo === 'egreso' && m.tipoOperacion !== 'devolucion_compra') || [];
  const devolucionesVenta = movimientos?.filter(m => m.tipoOperacion === 'devolucion_venta') || [];
  const totalDevVentas = devolucionesVenta.reduce((sum, d) => sum + (d.valor || 0), 0);
  const ventasBrutas = ingresos.reduce((sum, m) => sum + (m.valor || 0), 0);
  const ventasNetas = ventasBrutas - totalDevVentas;

  // Calcular ventas por canal
  const ventasPorCanal = agruparVentasPorCanal(movimientos);

  let costoVentasDirecto = 0, ventasConCosto = 0;
  ingresos.forEach(venta => {
    if (venta.costoUnitario && venta.cantidad) { costoVentasDirecto += (venta.costoUnitario * venta.cantidad); ventasConCosto++; }
  });
  const comprasInventario = egresos.filter(e => e.categoria === 'INVENTARIO' || e.categoria === 'COMPRA' || e.tipoOperacion === 'compra');
  const totalComprasInventario = comprasInventario.reduce((sum, c) => sum + (c.valor || 0), 0);
  const valorInventarioFinal = inventario?.reduce((sum, item) => sum + ((item.costoUnitario || 0) * (item.cantidad || 0)), 0) || 0;
  const costoVentasGlobal = totalComprasInventario - valorInventarioFinal;

  let costoVentas = 0;
  const cmvDirecto = (ventasConCosto > 0 && costoVentasDirecto > 0) ? costoVentasDirecto : 0;
  const cmvGlobal = (costoVentasGlobal > 0) ? costoVentasGlobal : 0;
  if (cmvDirecto > 0 && cmvGlobal > 0) costoVentas = (cmvDirecto + cmvGlobal) / 2;
  else if (cmvDirecto > 0) costoVentas = cmvDirecto;
  else if (cmvGlobal > 0) costoVentas = cmvGlobal;
  else costoVentas = ventasNetas * 0.55;

  const gastosOperativos = egresos.filter(e => e.categoria !== 'INVENTARIO' && e.categoria !== 'COMPRA' && e.tipoOperacion !== 'compra');
  const totalGastosOperativos = gastosOperativos.reduce((sum, g) => sum + (g.valor || 0), 0);

  let costosFijosAbsorbidos = 0;
  if (usuarioActual?.gastosFijosMensuales) {
    const fechas = movimientos?.map(m => m.fecha?.toDate ? m.fecha.toDate() : new Date(m.fecha)).filter(f => f && !isNaN(f));
    const inicioPeriodo = fechas?.length ? new Date(Math.min(...fechas)) : new Date();
    const finPeriodo = fechas?.length ? new Date(Math.max(...fechas)) : new Date();
    const diasPeriodo = Math.max(1, Math.ceil((finPeriodo - inicioPeriodo) / (1000 * 60 * 60 * 24)));
    const gastosFijosMensuales = parseFloat(usuarioActual.gastosFijosMensuales) || 0;
    costosFijosAbsorbidos = (gastosFijosMensuales / 30) * diasPeriodo;
  }

  const utilidadBruta = ventasNetas - costoVentas;
  const margenBruto = ventasNetas > 0 ? ((utilidadBruta / ventasNetas) * 100) : 0;
  const utilidadOperativa = utilidadBruta - totalGastosOperativos;
  const margenOperativo = ventasNetas > 0 ? ((utilidadOperativa / ventasNetas) * 100) : 0;
  const ebitda = utilidadOperativa + (totalGastosOperativos * 0.05);
  const margenEBITDA = ventasNetas > 0 ? ((ebitda / ventasNetas) * 100) : 0;
  const utilidadNeta = utilidadOperativa;
  const margenNeto = ventasNetas > 0 ? ((utilidadNeta / ventasNetas) * 100) : 0;
  const puntoEquilibrio = (margenBruto > 0 && totalGastosOperativos > 0) ? totalGastosOperativos / (margenBruto / 100) : 0;
  const roi = capitalInyectado > 0 ? ((utilidadNeta / capitalInyectado) * 100) : null;
  const requiereCapitalInicial = capitalInyectado <= 0 && utilidadNeta !== 0;

  const formatFecha = (fecha) => { if (!fecha) return ''; const d = fecha?.toDate ? fecha.toDate() : new Date(fecha); return d.toLocaleDateString(isEn ? 'en-US' : 'es-CO'); };
  const formatMoney = (valor) => { if (isNaN(valor) || valor === null || valor === undefined) return '$0'; return '$' + Math.round(valor).toLocaleString(); };
  const getFechasPeriodo = () => {
    const fechas = movimientos?.map(m => m.fecha?.toDate ? m.fecha.toDate() : new Date(m.fecha)).filter(f => f && !isNaN(f));
    const inicio = fechas?.length ? new Date(Math.min(...fechas)) : new Date();
    const fin = fechas?.length ? new Date(Math.max(...fechas)) : new Date();
    return { inicio, fin };
  };

  const data = {
    formatFecha, formatMoney, getFechasPeriodo,
    ventasNetas, costoVentas, utilidadBruta, margenBruto: margenBruto.toFixed(1),
    totalGastosOperativos, utilidadOperativa, margenOperativo: margenOperativo.toFixed(1),
    ebitda, margenEBITDA: margenEBITDA.toFixed(1),
    utilidadNeta, margenNeto: margenNeto.toFixed(1),
    puntoEquilibrio, roi: roi !== null ? roi.toFixed(1) : null,
    requiereCapitalInicial,
    ingresos, egresos, inventario, cuentasPorPagar,
    totalComprasInventario, totalDevVentas, tendenciaCrecimiento,
    capitalInyectado,
    movimientos,
    usuarioActual,
    procesosProduccion,
    costosFijosAbsorbidos,
    ventasPorCanal  // ← NUEVO
  };

  const dataFinanciera = {
    formatFecha,
    formatMoney,
    getFechasPeriodo,
    ventasNetas,
    costoVentas,
    utilidadBruta,
    gastosOperativos: totalGastosOperativos,
    utilidadNeta,
    utilidadOperativa,
    margenBrutoPct: margenBruto,
    margenNetoPct: margenNeto,
    totalDevVentas,
    puntoEquilibrio,
    roi: roi !== null ? roi.toFixed(1) : null,
    requiereCapitalInicial,
    ingresos,
    inventario,
    tendenciaCrecimiento,
    movimientos,
    usuarioActual,
    procesosProduccion,
    costosFijosAbsorbidos,
    ventasPorCanal  // ← NUEVO
  };

  const generadores = {
    pyg: generarPyG,
    auditoria: generarAuditoria,
    kpi: generarKPI,
    proyecciones: generarProyecciones,
    inventario: generarInventario,
    cuentasPagar: generarCuentasPagar,
    ventas: generarVentas,
    gastos: generarGastos,
    flujoCaja: generarFlujoCaja,
    roi: generarROI,
    compras: generarCompras
  };

  if (tipo === 'consolidado') {
    const reportes = ['pyg', 'auditoria', 'kpi', 'proyecciones', 'inventario', 'cuentasPagar', 'roi'];
    let df = null;
    reportes.forEach((r, idx) => {
      if (idx > 0) doc.addPage();
      if (r === 'pyg') {
        df = generadores[r](doc, data, isEn);
      } else if (['auditoria', 'kpi', 'proyecciones', 'flujoCaja'].includes(r)) {
        if (!df) {
          const tempDoc = crearDocBase();
          df = generarPyG(tempDoc, data, isEn);
        }
        generadores[r](doc, df, isEn);
      } else {
        generadores[r](doc, data, isEn);
      }
    });
  } else if (['auditoria', 'kpi', 'proyecciones', 'flujoCaja'].includes(tipo)) {
    const tempDoc = crearDocBase();
    const df = generarPyG(tempDoc, data, isEn);
    generadores[tipo](doc, df, isEn);
  } else if (generadores[tipo]) {
    generadores[tipo](doc, data, isEn);
  } else {
    doc.setFontSize(18);
    doc.text('STRATIUM GLOBAL AI', 105, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text(isEn ? 'Report type not implemented.' : 'Tipo de reporte no implementado.', 20, 65);
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text((isEn ? 'Page ' : 'Pagina ') + i + (isEn ? ' of ' : ' de ') + totalPages, 105, 290, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  return doc;
};

// ============================================================
// COMPONENTE VISUAL ExecutiveReport CON MODAL
// ============================================================
const ExecutiveReport = ({
  ventasTotales,
  gastosTotales,
  utilidadEstimada,
  onUpgradeClick,
  usuarioActual,
  movimientos,
  procesosProduccion,
  cuentasPorPagar,
  inventario,
  userPlan,
  hasGeneratedConsolidated,
  onUpdateStarterFlag,
  ventasPorCanal,     // ← NUEVO: Datos de ventas por canal
  utilidadPorCanal,   // ← NUEVO: Datos de utilidad por canal
  margenPorCanal      // ← NUEVO: Datos de margen por canal
}) => {
  const { idioma } = useTranslation();
  const isEn = idioma === 'en';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportesSeleccionados, setReportesSeleccionados] = useState({});
  const [warningMessage, setWarningMessage] = useState('');

  const porcentajeMargen = ventasTotales > 0 ? ((utilidadEstimada / ventasTotales) * 100).toFixed(1) : 0;

  const isStarter = userPlan === 'starter' || userPlan === 'STARTER';
  const starterAgotado = isStarter && hasGeneratedConsolidated;

  // Calcular ventas por canal si no se pasaron como props
  const canalesData = ventasPorCanal || agruparVentasPorCanal(movimientos);
  const canales = Object.keys(canalesData);
  
  // Encontrar mejor y peor canal
  let mejorCanal = '';
  let mejorCanalMargen = -Infinity;
  let peorCanal = '';
  let peorCanalMargen = Infinity;
  
  canales.forEach(canal => {
    const data = canalesData[canal];
    if (data.margen > mejorCanalMargen && data.ventas > 0) {
      mejorCanalMargen = data.margen;
      mejorCanal = canal;
    }
    if (data.margen < peorCanalMargen && data.ventas > 0) {
      peorCanalMargen = data.margen;
      peorCanal = canal;
    }
  });

  // Formateador de moneda para el componente visual
  const formatMoneyVisual = (valor) => {
    if (isNaN(valor) || valor === null || valor === undefined) return '$0';
    return '$' + Math.round(valor).toLocaleString();
  };

  const toggleReporte = (id) => {
    setWarningMessage('');

    if (starterAgotado) {
      const cantidadActual = Object.values(reportesSeleccionados).filter(Boolean).length;
      const yaMarcado = reportesSeleccionados[id];

      if (!yaMarcado && cantidadActual >= 1) {
        setWarningMessage(
          isEn
            ? 'You have exhausted your single courtesy Consolidated Report download in the Starter plan. Upgrade to ELITE plan for unlimited massive downloads.'
            : 'Has agotado tu unica descarga de cortesia del Reporte Consolidado en el plan Starter. Pasate al plan ELITE para descargas ilimitadas masivas.'
        );
        return;
      }
    }

    setReportesSeleccionados(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleConfirmar = () => {
    const seleccionados = Object.keys(reportesSeleccionados).filter(id => reportesSeleccionados[id]);

    if (seleccionados.length === 0) return;

    // Preparar datos para el PDF
    const ingresos = movimientos?.filter(m => m.tipo === 'ingreso' && m.tipoOperacion !== 'devolucion_venta') || [];
    const egresos = movimientos?.filter(m => m.tipo === 'egreso' && m.tipoOperacion !== 'devolucion_compra') || [];
    const devolucionesVenta = movimientos?.filter(m => m.tipoOperacion === 'devolucion_venta') || [];
    const totalDevVentas = devolucionesVenta.reduce((sum, d) => sum + (d.valor || 0), 0);
    const ventasBrutas = ingresos.reduce((sum, m) => sum + (m.valor || 0), 0);
    const ventasNetas = ventasBrutas - totalDevVentas;

    let costoVentasDirecto = 0, ventasConCosto = 0;
    ingresos.forEach(venta => {
      if (venta.costoUnitario && venta.cantidad) {
        costoVentasDirecto += (venta.costoUnitario * venta.cantidad);
        ventasConCosto++;
      }
    });

    const comprasInventario = egresos.filter(e => e.categoria === 'INVENTARIO' || e.categoria === 'COMPRA' || e.tipoOperacion === 'compra');
    const totalComprasInventario = comprasInventario.reduce((sum, c) => sum + (c.valor || 0), 0);
    const valorInventarioFinal = inventario?.reduce((sum, item) => sum + ((item.costoUnitario || 0) * (item.cantidad || 0)), 0) || 0;
    const costoVentasGlobal = totalComprasInventario - valorInventarioFinal;

    let costoVentas = 0;
    const cmvDirecto = (ventasConCosto > 0 && costoVentasDirecto > 0) ? costoVentasDirecto : 0;
    const cmvGlobal = (costoVentasGlobal > 0) ? costoVentasGlobal : 0;
    if (cmvDirecto > 0 && cmvGlobal > 0) costoVentas = (cmvDirecto + cmvGlobal) / 2;
    else if (cmvDirecto > 0) costoVentas = cmvDirecto;
    else if (cmvGlobal > 0) costoVentas = cmvGlobal;
    else costoVentas = ventasNetas * 0.55;

    const gastosOperativosFiltrados = egresos.filter(e => e.categoria !== 'INVENTARIO' && e.categoria !== 'COMPRA' && e.tipoOperacion !== 'compra');
    const totalGastosOperativos = gastosOperativosFiltrados.reduce((sum, g) => sum + (g.valor || 0), 0);

    const utilidadBruta = ventasNetas - costoVentas;
    const margenBruto = ventasNetas > 0 ? ((utilidadBruta / ventasNetas) * 100) : 0;
    const utilidadOperativa = utilidadBruta - totalGastosOperativos;
    const ebitda = utilidadOperativa + (totalGastosOperativos * 0.05);
    const utilidadNeta = utilidadOperativa;
    const margenNeto = ventasNetas > 0 ? ((utilidadNeta / ventasNetas) * 100) : 0;
    const puntoEquilibrio = (margenBruto > 0 && totalGastosOperativos > 0) ? totalGastosOperativos / (margenBruto / 100) : 0;
    const capitalInyectado = usuarioActual?.aportesPersonales || 0;
    const roi = capitalInyectado > 0 ? ((utilidadNeta / capitalInyectado) * 100) : null;

    const formatFecha = (fecha) => { if (!fecha) return ''; const d = fecha?.toDate ? fecha.toDate() : new Date(fecha); return d.toLocaleDateString(isEn ? 'en-US' : 'es-CO'); };
    const formatMoney = (valor) => { if (isNaN(valor) || valor === null || valor === undefined) return '$0'; return '$' + Math.round(valor).toLocaleString(); };
    const getFechasPeriodo = () => {
      const fechas = movimientos?.map(m => m.fecha?.toDate ? m.fecha.toDate() : new Date(m.fecha)).filter(f => f && !isNaN(f));
      const inicio = fechas?.length ? new Date(Math.min(...fechas)) : new Date();
      const fin = fechas?.length ? new Date(Math.max(...fechas)) : new Date();
      return { inicio, fin };
    };

    // Calcular ventas por canal para el PDF
    const ventasPorCanalData = agruparVentasPorCanal(movimientos);

    const data = {
      movimientos: movimientos,
      procesosProduccion: procesosProduccion,
      usuarioActual: usuarioActual,
      formatFecha, formatMoney, getFechasPeriodo,
      ventasNetas, costoVentas, utilidadBruta,
      margenBruto: margenBruto.toFixed(1),
      totalGastosOperativos, utilidadOperativa,
      margenOperativo: (ventasNetas > 0 ? ((utilidadOperativa / ventasNetas) * 100) : 0).toFixed(1),
      ebitda, margenEBITDA: (ventasNetas > 0 ? ((ebitda / ventasNetas) * 100) : 0).toFixed(1),
      utilidadNeta, margenNeto: margenNeto.toFixed(1),
      puntoEquilibrio, roi: roi !== null ? roi.toFixed(1) : null,
      requiereCapitalInicial: capitalInyectado <= 0 && utilidadNeta !== 0,
      ingresos, egresos, inventario, cuentasPorPagar,
      totalComprasInventario, totalDevVentas, tendenciaCrecimiento: 1.08,
      capitalInyectado,
      compras: comprasInventario,
      ventasPorCanal: ventasPorCanalData  // ← NUEVO: Pasar datos de canales al PDF
    };

    generarReportesSeleccionados(seleccionados, data, isEn);

    if (isStarter && !hasGeneratedConsolidated && seleccionados.length > 1) {
      if (onUpdateStarterFlag) onUpdateStarterFlag();
    }

    setIsModalOpen(false);
    setReportesSeleccionados({});
    setWarningMessage('');
  };

  const cantidadSeleccionados = Object.values(reportesSeleccionados).filter(Boolean).length;

  return (
    <>
      <div className="bg-[#1e293b] rounded-2xl p-6 mb-4 border border-blue-900/30">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          📋 {isEn ? 'Audit Report' : 'Dictamen de Auditoria'}
        </h2>
        <div className="bg-[#0f172a] rounded-xl p-4 font-mono text-sm text-gray-300">
          <p className="text-gray-400">{isEn ? '📊 EXECUTIVE REPORT' : '📊 REPORTE EJECUTIVO'}</p>
          <p>━━━━━━━━━━━━━━━━━━━━━</p>
          <p>📈 {isEn ? 'Sales' : 'Ventas'}: ${ventasTotales?.toLocaleString() || 0}</p>
          <p>📉 {isEn ? 'Expenses' : 'Gastos'}: ${gastosTotales?.toLocaleString() || 0}</p>
          <p>💰 {isEn ? 'Net Profit' : 'Utilidad Neta'}: ${utilidadEstimada?.toLocaleString() || 0}</p>
          <p>📊 {isEn ? 'Net Margin' : 'Margen Neto'}: {porcentajeMargen}%</p>
          
          {/* ============================================================
              NUEVA SECCIÓN: VENTAS POR CANAL
              ============================================================ */}
          {canales.length > 0 && (
            <>
              <p>━━━━━━━━━━━━━━━━━━━━━</p>
              <p className="text-gray-400 text-xs mt-2">{isEn ? '📊 Sales by Channel' : '📊 Ventas por Canal'}</p>
              {canales.map(canal => {
                const data = canalesData[canal];
                const ventas = data.ventas || 0;
                const porcentaje = ventasTotales > 0 ? ((ventas / ventasTotales) * 100) : 0;
                const margen = data.margen || 0;
                const emoji = margen >= 50 ? '✅' : margen >= 30 ? '⚠️' : '❌';
                
                return (
                  <div key={canal} className="flex justify-between items-center text-xs py-0.5">
                    <span className="text-gray-400">{emoji} {canal}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-white font-medium">{formatMoneyVisual(ventas)}</span>
                      <span className="text-gray-500">({porcentaje.toFixed(1)}%)</span>
                      <span className={`font-bold ${margen >= 50 ? 'text-green-400' : margen >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {margen.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
              
              {/* Recomendación de canal */}
              {mejorCanal && (
                <div className="mt-2 pt-2 border-t border-slate-800">
                  <p className="text-green-400 text-xs font-bold">
                    ✅ {isEn ? 'Best Channel' : 'Mejor Canal'}: {mejorCanal} ({mejorCanalMargen.toFixed(1)}% {isEn ? 'margin' : 'margen'})
                  </p>
                  {peorCanal && peorCanalMargen < 30 && (
                    <p className="text-yellow-400 text-xs mt-0.5">
                      ⚠️ {isEn ? 'Channel with low margin' : 'Canal con bajo margen'}: {peorCanal} ({peorCanalMargen.toFixed(1)}%) - {isEn ? 'Review pricing or commissions' : 'Revisar precios o comisiones'}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          
          <p>━━━━━━━━━━━━━━━━━━━━━</p>
          <p className="text-green-400 mt-2">
            {isEn ? '📢 Healthy profitability. Look to optimize expenses to improve.' : '📢 Rentabilidad saludable. Busca optimizar gastos para mejorar.'}
          </p>
          <div className="mt-4 border-t border-slate-800 pt-2 text-xs text-gray-500">
            <p>🔓 {isEn ? 'Want to know your Contribution Margin, EBITDA and ROI?' : '¿Quieres conocer tu Margen de Contribucion, EBITDA y ROI?'}</p>
            <p onClick={onUpgradeClick} className="text-cyan-400 font-semibold cursor-pointer mt-1 hover:text-cyan-300 transition">
              💡 {isEn ? 'Upgrade to Business Plan and discover the 6 real profitability margins.' : 'Actualiza al Plan Business y descubre los 6 margenes reales de rentabilidad.'}
            </p>
          </div>
        </div>

        <button
          onClick={() => { setIsModalOpen(true); setWarningMessage(''); }}
          className="w-full mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-all"
        >
          📋 {isEn ? 'Export Analytical Reports' : 'Exportar Reportes Analiticos'}
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#1e293b] rounded-2xl p-6 max-w-2xl w-full border border-blue-900/30 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {isEn ? 'Select Reports to Export' : 'Seleccionar Reportes a Exportar'}
              </h2>
              <button onClick={() => { setIsModalOpen(false); setWarningMessage(''); setReportesSeleccionados({}); }} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>

            {starterAgotado && (
              <div className="mb-4 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl text-cyan-300 text-sm">
                ℹ️ {isEn
                  ? 'Starter plan: You can only select 1 report at a time. Upgrade to ELITE for unlimited consolidated downloads.'
                  : 'Plan Starter: Solo puedes seleccionar 1 reporte a la vez. Pasate a ELITE para descargas consolidadas ilimitadas.'}
              </div>
            )}

            {warningMessage && (
              <div className="mb-4 p-4 bg-red-900/30 border border-red-500/50 rounded-xl text-red-300 text-sm">
                ⚠️ {warningMessage}
              </div>
            )}

            <div className="space-y-3 mb-6">
              {REPORTES_DISPONIBLES.map(reporte => {
                const estaMarcado = reportesSeleccionados[reporte.id] || false;
                const deshabilitado = starterAgotado && !estaMarcado && cantidadSeleccionados >= 1;

                return (
                  <label
                    key={reporte.id}
                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                      deshabilitado
                        ? 'bg-slate-800/30 border-slate-700 opacity-50 cursor-not-allowed'
                        : estaMarcado
                        ? 'bg-cyan-500/20 border-cyan-500/50'
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={estaMarcado}
                      onChange={() => !deshabilitado && toggleReporte(reporte.id)}
                      disabled={deshabilitado}
                      className="w-5 h-5 rounded border-slate-600 bg-slate-900 text-cyan-500 focus:ring-cyan-500 focus:ring-2"
                    />
                    <span className="text-white font-medium">{isEn ? reporte.en : reporte.es}</span>
                  </label>
                );
              })}
            </div>

            {cantidadSeleccionados > 0 && (
              <div className="mb-4 p-3 bg-slate-800/50 rounded-lg text-sm text-gray-300">
                {isEn ? 'Selected: ' : 'Seleccionados: '}
                <span className="text-cyan-400 font-bold">{cantidadSeleccionados}</span>
                {isEn ? ' report(s)' : ' reporte(s)'}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setIsModalOpen(false); setWarningMessage(''); setReportesSeleccionados({}); }}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-4 rounded-lg transition-all"
              >
                {isEn ? 'Cancel' : 'Cancelar'}
              </button>
              <button
                onClick={handleConfirmar}
                disabled={cantidadSeleccionados === 0}
                className={`flex-1 font-bold py-3 px-4 rounded-lg transition-all ${
                  cantidadSeleccionados === 0
                    ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white'
                }`}
              >
                {isEn ? 'Confirm & Print' : 'Confirmar e Imprimir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExecutiveReport;

